import type { ReactNode, CSSProperties } from 'react';
import { useScrollReveal } from '../../hooks/useScrollReveal';

export type RevealVariant = 'up' | 'left' | 'right' | 'scale' | 'fade';

const VARIANT_CLASS: Record<RevealVariant, string> = {
  up: 'reveal',
  left: 'reveal-left',
  right: 'reveal-right',
  scale: 'reveal-scale',
  fade: 'reveal-fade',
};

interface RevealProps {
  children: ReactNode;
  /** Animation direction/style. Default 'up'. */
  variant?: RevealVariant;
  /** Stagger delay step (1–8 → 0.08s increments). Default 0. */
  delay?: number;
  className?: string;
  /** IntersectionObserver visibility threshold. Default 0.15. */
  threshold?: number;
  /** Animate only once (default) or every time it enters the viewport. */
  once?: boolean;
  style?: CSSProperties;
}

/**
 * Declarative scroll-reveal wrapper. Uses the shared IntersectionObserver hook
 * and the .reveal* classes in index.css. Honours prefers-reduced-motion via CSS.
 *
 *   <Reveal variant="left" delay={2}>…</Reveal>
 */
export default function Reveal({
  children,
  variant = 'up',
  delay = 0,
  className = '',
  threshold = 0.15,
  once = true,
  style,
}: RevealProps) {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>({ threshold, once });

  const classes = [
    VARIANT_CLASS[variant],
    delay > 0 ? `reveal-delay-${delay}` : '',
    isVisible ? 'visible' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={ref} className={classes} style={style}>
      {children}
    </div>
  );
}
